import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Button, Menu, Tooltip, theme } from 'antd';
import type { MenuProps } from 'antd';
import { EnvironmentFilled, EnvironmentOutlined, HomeOutlined, MinusOutlined, PictureOutlined, PlusOutlined, RedoOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';


interface Location {
  id: string;
  name: string;
  xCoordinate?: number;
  yCoordinate?: number;
  equipmentCount: number;
  objectCount?: number;
  mapMode?: 'image' | 'building' | 'folder' | 'geo' | null;
  level: number;
  description?: string;
  icon?: string | null;
  categoryId?: string | null;
  thumbnailFileId?: string | null;
}

interface LocationCategory {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
  isActive: boolean;
}

type UnplacedGridItem = {
  id: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  badgeCount: number;
  tooltip?: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
};

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof EnvironmentOutlined>>;

interface FactoryLayoutViewProps {
  locations: Location[];
  selectedLocationId?: string | null;
  onLocationClick?: (location: Location) => void;
  onLocationDoubleClick?: (location: Location) => void;
  onBackgroundClick?: () => void;
  onRequestMapImageSettings?: () => void;
  backgroundImageUrl?: string | null;
  backgroundFillColor?: string | null;
  mapMode?: 'image' | 'building' | 'folder' | 'geo' | null;
  showTitle?: boolean;
  categories?: LocationCategory[];
  thumbnailUrlByFileId?: Record<string, string>;
  isBulkPositionEditMode?: boolean;
  onLocationMove?: (locationId: string, x: number, y: number) => void;
  onLocationsMove?: (updates: Array<{ id: string; x: number; y: number }>) => void;
  onRequestCreateAt?: (x: number, y: number) => void;
  unplacedGridItems?: UnplacedGridItem[];
  bottomLeftOverlay?: React.ReactNode;
  bottomRightOverlay?: React.ReactNode;
}

export function FactoryLayoutViewResponsive({
  locations,
  selectedLocationId,
  onLocationClick,
  onLocationDoubleClick,
  onBackgroundClick,
  onRequestMapImageSettings,
  backgroundImageUrl,
  backgroundFillColor,
  mapMode = 'image',
  showTitle = true,
  categories = [],
  thumbnailUrlByFileId = {},
  isBulkPositionEditMode = false,
  onLocationMove,
  onLocationsMove: _onLocationsMove,
  onRequestCreateAt,
  unplacedGridItems = [],
  bottomLeftOverlay,
  bottomRightOverlay,
}: FactoryLayoutViewProps) {
  const { token } = theme.useToken();
  const locationMarkerVisualScale = 0.7;
  const scaleMarkerPx = (px: number) => Math.round(px * locationMarkerVisualScale);
  const locationMarkerPinOpacity = 0.7;

  const locationMarkerFolderIconSize = scaleMarkerPx(56);
  const locationMarkerBoxSize = locationMarkerFolderIconSize;
  const locationMarkerIconBoxSize = scaleMarkerPx(30 * 0.9);
  const locationMarkerIconGlyphSize = scaleMarkerPx(28 * 0.9);
  const locationMarkerIconYOffset = scaleMarkerPx(2);
  const locationMarkerIconPedestalSize = locationMarkerIconBoxSize + scaleMarkerPx(10);
  const locationMarkerIconContainerTranslateY = scaleMarkerPx(8);
  const locationMarkerIconPedestalCenterOffsetY = scaleMarkerPx(4);
  const locationMarkerMaxScale = 1.3;
  const locationMarkerBadgeSize = scaleMarkerPx(22);
  const locationMarkerBadgeFontSize = scaleMarkerPx(11);
  const locationMarkerBadgeBorderWidth = Math.max(1, scaleMarkerPx(2));
  const locationMarkerBadgeOffsetX = scaleMarkerPx(-6);
  const locationMarkerBadgeOffsetY = scaleMarkerPx(-6 + 5 + 2 + 3);
  const locationMarkerLabelFontSize = Math.max(1, Math.round(scaleMarkerPx(11) * 1.2));
  const locationPinLabelTop = scaleMarkerPx(-20) - 2;
  const locationMarkerUnplacedTileSize = scaleMarkerPx(46);
  const locationMarkerUnplacedTileBorderRadius = scaleMarkerPx(10);
  const locationMarkerSelectionIndicatorSize = {
    top: scaleMarkerPx(-12),
    left: scaleMarkerPx(-15),
    width: locationMarkerBoxSize + scaleMarkerPx(30),
    height: locationMarkerBoxSize + scaleMarkerPx(25),
  };

  const [hoveredLocation, setHoveredLocation] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const displaySizeRef = useRef(displaySize);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [viewport, setViewport] = useState<{ scale: number; panX: number; panY: number }>({
    scale: 1,
    panX: 0,
    panY: 0,
  });

  useEffect(() => {
    displaySizeRef.current = displaySize;
  }, [displaySize]);

  const markerCompensationScale = viewport.scale > locationMarkerMaxScale
    ? locationMarkerMaxScale / viewport.scale
    : 1;
  const viewportRef = useRef(viewport);
  const zoomAroundRef = useRef<((targetScale: number, screenX: number, screenY: number) => void) | null>(null);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  const [panning, setPanning] = useState<{
    startClientX: number;
    startClientY: number;
    startPanX: number;
    startPanY: number;
    startLocalX: number;
    startLocalY: number;
    storedX: number | null;
    storedY: number | null;
  } | null>(null);

  const [contextMenu, setContextMenu] = useState<{
    localX: number;
    localY: number;
    storedX: number;
    storedY: number;
  } | null>(null);
  const contextMenuRef = useRef(contextMenu);
  const contextMenuElRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contextMenuRef.current = contextMenu;
  }, [contextMenu]);

  const [draggingMarker, setDraggingMarker] = useState<{
    id: string;
    startClientX: number;
    startClientY: number;
    startPos: { x: number; y: number };
    sourceIsUnplaced: boolean;
  } | null>(null);
  const [tempPositions, setTempPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [pendingCommitById, setPendingCommitById] = useState<Record<string, { x: number; y: number; startedAtMs: number }>>({});

  const draggingMarkerRef = useRef(draggingMarker);
  const tempPositionsRef = useRef(tempPositions);
  const suppressNextMarkerClickRef = useRef(false);

  useEffect(() => {
    draggingMarkerRef.current = draggingMarker;
  }, [draggingMarker]);

  useEffect(() => {
    tempPositionsRef.current = tempPositions;
  }, [tempPositions]);

  useEffect(() => {
    if (!isBulkPositionEditMode) {
      if (Object.keys(pendingCommitById).length > 0) {
        setPendingCommitById({});
      }
      return;
    }

    const nowMs = Date.now();
    const timeoutMs = 8000;
    const epsilon = 0.001;

    const idsToClear: string[] = [];
    for (const [id, pending] of Object.entries(pendingCommitById)) {
      const location = locations.find((l) => l.id === id);
      const storedX = location?.xCoordinate;
      const storedY = location?.yCoordinate;

      const isConfirmed = typeof storedX === 'number'
        && typeof storedY === 'number'
        && Math.abs(storedX - pending.x) <= epsilon
        && Math.abs(storedY - pending.y) <= epsilon;

      const isDiverged = typeof storedX === 'number'
        && typeof storedY === 'number'
        && !isConfirmed;

      const isExpired = nowMs - pending.startedAtMs > timeoutMs;

      if (isConfirmed || isDiverged || isExpired) {
        idsToClear.push(id);
      }
    }

    if (idsToClear.length === 0) {
      return;
    }

    setPendingCommitById((current) => {
      const next = { ...current };
      idsToClear.forEach((id) => {
        delete next[id];
      });
      return next;
    });

    setTempPositions((current) => {
      const next = { ...current };
      idsToClear.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  }, [isBulkPositionEditMode, locations, pendingCommitById]);

  const categoryById = useMemo(() => {
    const map = new Map<string, LocationCategory>();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  const hasStoredCoordinates = (location: Location): boolean =>
    typeof location.xCoordinate === 'number' && typeof location.yCoordinate === 'number';

  const unplacedLocations = useMemo(() => {
    return locations
      .filter((location) => !hasStoredCoordinates(location))
      .slice()
      .sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level;
        return a.name.localeCompare(b.name, 'ja');
      });
  }, [locations]);

  const unplacedIndexById = useMemo(() => {
    const map = new Map<string, number>();
    unplacedLocations.forEach((location, index) => map.set(location.id, index));
    return map;
  }, [unplacedLocations]);

  const defaultBackgroundImageUrl = '/images/factory/default-location-map.png';
  const resolvedBackgroundImageUrl = mapMode === 'folder'
    ? null
    : backgroundImageUrl === null
      ? null
      : backgroundImageUrl || defaultBackgroundImageUrl;

  const resolvedBackgroundFill = useMemo(() => {
    const parseHex = (hex: string): { r: number; g: number; b: number } | null => {
      const trimmed = hex.trim();
      if (!/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
        return null;
      }
      const r = parseInt(trimmed.slice(1, 3), 16);
      const g = parseInt(trimmed.slice(3, 5), 16);
      const b = parseInt(trimmed.slice(5, 7), 16);
      return { r, g, b };
    };

    const mix = (value: number, target: number, amount: number) =>
      Math.round(value + (target - value) * amount);

    const toRgba = (rgb: { r: number; g: number; b: number }, a: number) =>
      `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;

    const base = backgroundFillColor ? parseHex(backgroundFillColor) : null;
    if (!base) {
      return `linear-gradient(135deg, rgba(0,0,0,0.06), rgba(0,0,0,0.02))`;
    }

    const light = { r: mix(base.r, 255, 0.22), g: mix(base.g, 255, 0.22), b: mix(base.b, 255, 0.22) };
    const dark = { r: mix(base.r, 0, 0.14), g: mix(base.g, 0, 0.14), b: mix(base.b, 0, 0.14) };

    return [
      `radial-gradient(120% 120% at 20% 10%, ${toRgba(light, 0.92)} 0%, ${toRgba(base, 0.9)} 46%, ${toRgba(dark, 0.92)} 100%)`,
      `linear-gradient(135deg, ${toRgba(light, 0.16)}, ${toRgba(dark, 0.12)})`,
    ].join(', ');
  }, [backgroundFillColor]);

  useEffect(() => {
    if (!resolvedBackgroundImageUrl) {
      setImageSize({ width: 0, height: 0 });
      setDisplaySize({ width: 0, height: 0 });
    }
  }, [resolvedBackgroundImageUrl]);

  useEffect(() => {
    setViewport({ scale: 1, panX: 0, panY: 0 });
  }, [resolvedBackgroundImageUrl]);

  // 画像の実際のサイズを取得
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const clamp = useCallback((value: number, min: number, max: number): number => Math.min(max, Math.max(min, value)), []);

  const getEffectiveStorageSize = useCallback((): { width: number; height: number } => {
    if (imageSize.width > 0 && imageSize.height > 0) {
      return { width: imageSize.width, height: imageSize.height };
    }
    return { width: 1000, height: 1000 };
  }, [imageSize.height, imageSize.width]);

  const computeContainLayout = useCallback(
    (containerWidth: number, containerHeight: number): { contentWidth: number; contentHeight: number; offsetX: number; offsetY: number } => {
      let contentWidth = containerWidth;
      let contentHeight = containerHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (imageSize.width > 0 && imageSize.height > 0 && containerWidth > 0 && containerHeight > 0) {
        const imageAspect = imageSize.width / imageSize.height;
        const containerAspect = containerWidth / containerHeight;

        if (imageAspect > containerAspect) {
          contentWidth = containerWidth;
          contentHeight = containerWidth / imageAspect;
          offsetY = (containerHeight - contentHeight) / 2;
        } else {
          contentHeight = containerHeight;
          contentWidth = containerHeight * imageAspect;
          offsetX = (containerWidth - contentWidth) / 2;
        }
      }

      return { contentWidth, contentHeight, offsetX, offsetY };
    },
    [imageSize.height, imageSize.width]
  );

  const screenToStoredCoordinatesForDisplaySize = useCallback(
    (screenX: number, screenY: number, containerWidth: number, containerHeight: number): { x: number; y: number } | null => {
      if (!containerWidth || !containerHeight) {
        return null;
      }

      const { width: storageWidth, height: storageHeight } = getEffectiveStorageSize();
      const { contentWidth, contentHeight, offsetX, offsetY } = computeContainLayout(containerWidth, containerHeight);

      if (!contentWidth || !contentHeight) {
        return null;
      }

      const relativeX = clamp((screenX - offsetX) / contentWidth, 0, 1);
      const relativeY = clamp((screenY - offsetY) / contentHeight, 0, 1);

      const x = Math.round(relativeX * storageWidth);
      const y = Math.round(relativeY * storageHeight);

      return {
        x: clamp(x, 0, storageWidth),
        y: clamp(y, 0, storageHeight),
      };
    },
    [clamp, computeContainLayout, getEffectiveStorageSize]
  );

  const getLocationBasePositionForDisplaySize = useCallback(
    (x?: number, y?: number, containerWidth?: number, containerHeight?: number): { x: number; y: number } | null => {
      if (x === undefined || y === undefined) {
        return null;
      }
      if (!containerWidth || !containerHeight) {
        return null;
      }

      const { width: storageWidth, height: storageHeight } = getEffectiveStorageSize();

      let relativeX = x;
      let relativeY = y;
      if (x > 1 || y > 1) {
        relativeX = x / storageWidth;
        relativeY = y / storageHeight;
      }

      const { contentWidth, contentHeight, offsetX, offsetY } = computeContainLayout(containerWidth, containerHeight);
      if (!contentWidth || !contentHeight) {
        return null;
      }

      const screenPosX = offsetX + relativeX * contentWidth;
      const screenPosY = offsetY + relativeY * contentHeight;
      return { x: screenPosX, y: screenPosY };
    },
    [computeContainLayout, getEffectiveStorageSize]
  );

  const pendingResizeAnchorRef = useRef<{ storedX: number; storedY: number; scale: number } | null>(null);

  // コンテナのサイズを監視（window resize だけだと、パネル幅変更/ブラウザズーム等で取りこぼす）
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const el = containerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      const width = entry?.contentRect?.width ?? 0;
      const height = entry?.contentRect?.height ?? 0;
      if (!width || !height) {
        return;
      }

      const prev = displaySizeRef.current;
      const currentViewport = viewportRef.current;
      const changed = Math.abs(prev.width - width) > 0.5 || Math.abs(prev.height - height) > 0.5;
      if (changed && prev.width > 0 && prev.height > 0 && currentViewport.scale > 1) {
        const prevCenterX = prev.width / 2;
        const prevCenterY = prev.height / 2;
        const baseX = (prevCenterX - currentViewport.panX) / (currentViewport.scale || 1);
        const baseY = (prevCenterY - currentViewport.panY) / (currentViewport.scale || 1);
        const stored = screenToStoredCoordinatesForDisplaySize(baseX, baseY, prev.width, prev.height);
        if (stored) {
          pendingResizeAnchorRef.current = { storedX: stored.x, storedY: stored.y, scale: currentViewport.scale };
        }
      }

      setDisplaySize({ width, height });
    });

    resizeObserver.observe(el);

    // 初回も一度反映（ResizeObserver 発火前のタイミング対策）
    const rect = el.getBoundingClientRect();
    if (rect.width && rect.height) {
      setDisplaySize({ width: rect.width, height: rect.height });
    }

    return () => resizeObserver.disconnect();
  }, [screenToStoredCoordinatesForDisplaySize]);

  // 相対座標（0-1の範囲）を画面座標に変換
  const getLocationScreenPosition = (x?: number, y?: number) => {
    const { width: containerWidth, height: containerHeight } = getEffectiveDisplaySize();
    return getLocationBasePositionForDisplaySize(x, y, containerWidth, containerHeight);
  };

  const getEffectiveDisplaySize = useCallback((): { width: number; height: number } => {
    const fallbackWidth = containerRef.current?.getBoundingClientRect().width || 0;
    const fallbackHeight = containerRef.current?.getBoundingClientRect().height || 0;
    return {
      width: displaySize.width || fallbackWidth,
      height: displaySize.height || fallbackHeight,
    };
  }, [displaySize.height, displaySize.width]);

  const screenToStoredCoordinates = useCallback((screenX: number, screenY: number): { x: number; y: number } | null => {
    const { width: containerWidth, height: containerHeight } = getEffectiveDisplaySize();
    if (!containerWidth || !containerHeight) {
      return null;
    }
    return screenToStoredCoordinatesForDisplaySize(screenX, screenY, containerWidth, containerHeight);
  }, [getEffectiveDisplaySize, screenToStoredCoordinatesForDisplaySize]);

  const clampViewport = useCallback((next: { scale: number; panX: number; panY: number }) => {
    const scale = clamp(next.scale, 1, 4);
    if (scale === 1) {
      return { scale: 1, panX: 0, panY: 0 };
    }

    const { width, height } = getEffectiveDisplaySize();
    if (!width || !height) {
      return { scale, panX: 0, panY: 0 };
    }

    const scaledW = width * scale;
    const scaledH = height * scale;

    const minX = Math.min(0, width - scaledW);
    const minY = Math.min(0, height - scaledH);

    return {
      scale,
      panX: clamp(next.panX, minX, 0),
      panY: clamp(next.panY, minY, 0),
    };
  }, [clamp, getEffectiveDisplaySize]);

  useEffect(() => {
    const pending = pendingResizeAnchorRef.current;
    if (!pending) {
      return;
    }
    pendingResizeAnchorRef.current = null;

    setViewport((current) => {
      if (current.scale !== pending.scale) {
        return current;
      }
      const { width, height } = displaySizeRef.current;
      if (!width || !height) {
        return current;
      }

      const basePos = getLocationBasePositionForDisplaySize(pending.storedX, pending.storedY, width, height);
      if (!basePos) {
        return current;
      }

      const centerX = width / 2;
      const centerY = height / 2;
      const panX = centerX - basePos.x * current.scale;
      const panY = centerY - basePos.y * current.scale;
      return clampViewport({ scale: current.scale, panX, panY });
    });
  }, [clampViewport, displaySize.height, displaySize.width, getLocationBasePositionForDisplaySize]);

  useEffect(() => {
    // サイズ変化後に pan がはみ出した場合でも、空白が出ないようにクランプ
    setViewport((current) => clampViewport(current));
  }, [clampViewport, displaySize.height, displaySize.width]);

  const zoomAround = useCallback((targetScale: number, screenX: number, screenY: number) => {
    setViewport((current) => {
      const currentScale = current.scale || 1;
      const nextScale = clamp(targetScale, 1, 4);
      if (nextScale === currentScale) {
        return current;
      }

      const baseX = (screenX - current.panX) / currentScale;
      const baseY = (screenY - current.panY) / currentScale;

      const panX = screenX - baseX * nextScale;
      const panY = screenY - baseY * nextScale;

      return clampViewport({ scale: nextScale, panX, panY });
    });
  }, [clamp, clampViewport]);

  useEffect(() => {
    zoomAroundRef.current = zoomAround;
  }, [zoomAround]);

  // マップ領域上のホイールでページスクロールを抑止し、ズーム操作として扱う（React の onWheel は passive のため使わない）
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setContextMenu(null);

      const rect = el.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const factor = event.deltaY > 0 ? 1 / 1.15 : 1.15;
      zoomAroundRef.current?.(viewportRef.current.scale * factor, x, y);
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => {
      el.removeEventListener('wheel', onWheel, true);
    };
  }, []);

  const getUnplacedScreenPosition = (index: number): { x: number; y: number } | null => {
    const { width, height } = getEffectiveDisplaySize();
    if (!width || !height) {
      return null;
    }

    // 左端のアイコンがはみ出さないよう、中心座標（x）を「左余白 + 半径」基準で算出する
    const leftPadding = 16;
    const bottomPadding = 26;
    // 未配置グリッドは見やすさを保ちつつ、横幅/縦幅を少し詰める（約2割）
    const cellWidth = 90;
    const cellHeight = 60;

    const maxCols = Math.max(1, Math.floor((width - leftPadding * 2) / cellWidth));
    const col = index % maxCols;
    const row = Math.floor(index / maxCols);

    const x = leftPadding + col * cellWidth + locationMarkerBoxSize / 2;
    const y = height - bottomPadding - row * cellHeight;

    return { x, y };
  };

  const unplacedGridOffset = unplacedGridItems.length;

  useEffect(() => {
    if (!isBulkPositionEditMode) {
      setDraggingMarker(null);
      setTempPositions((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    if (!draggingMarker) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const markerState = draggingMarkerRef.current;
      if (markerState) {
        const scale = viewportRef.current.scale || 1;
        const dxRaw = event.clientX - markerState.startClientX;
        const dyRaw = event.clientY - markerState.startClientY;
        const dx = markerState.sourceIsUnplaced ? dxRaw : dxRaw / scale;
        const dy = markerState.sourceIsUnplaced ? dyRaw : dyRaw / scale;

        setTempPositions((prev) => ({
          ...prev,
          [markerState.id]: {
            x: markerState.startPos.x + dx,
            y: markerState.startPos.y + dy,
          },
        }));
      }
    };

    const handlePointerUp = () => {
      const markerState = draggingMarkerRef.current;
      if (markerState) {
        const latestTemp = tempPositionsRef.current;
        const finalPos = latestTemp[markerState.id] ?? markerState.startPos;
        const movedDistance = Math.hypot(finalPos.x - markerState.startPos.x, finalPos.y - markerState.startPos.y);
        let shouldKeepTempPosition = false;
        if (movedDistance >= 2) {
          suppressNextMarkerClickRef.current = true;
          const { scale, panX, panY } = viewportRef.current;
          const baseX = markerState.sourceIsUnplaced ? (finalPos.x - panX) / (scale || 1) : finalPos.x;
          const baseY = markerState.sourceIsUnplaced ? (finalPos.y - panY) / (scale || 1) : finalPos.y;
          const stored = screenToStoredCoordinates(baseX, baseY);
          if (stored) {
            onLocationMove?.(markerState.id, stored.x, stored.y);
            // サーバ反映（またはキャッシュ更新）まで、見た目は落とした位置に固定してシームレスにする
            shouldKeepTempPosition = true;
            setPendingCommitById((current) => ({
              ...current,
              [markerState.id]: { x: stored.x, y: stored.y, startedAtMs: Date.now() },
            }));
          }
        }

        setDraggingMarker(null);
        setTempPositions((prev) => {
          const next = { ...prev };
          // pending がある間は保持して、props側が追いついたらeffectで消す
          if (!shouldKeepTempPosition) {
            delete next[markerState.id];
          }
          return next;
        });
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [draggingMarker, isBulkPositionEditMode, onLocationMove, screenToStoredCoordinates]);

  useEffect(() => {
    if (!panning) {
      return;
    }

    let moved = false;

    const handlePointerMove = (event: PointerEvent) => {
      const dx = event.clientX - panning.startClientX;
      const dy = event.clientY - panning.startClientY;

      if (!moved) {
        const distance = Math.hypot(dx, dy);
        if (distance < 3) {
          return;
        }
        moved = true;
      }

      setViewport((current) =>
        clampViewport({
          scale: current.scale,
          panX: panning.startPanX + dx,
          panY: panning.startPanY + dy,
        })
      );
    };

    const handlePointerUp = () => {
      if (!moved && !isBulkPositionEditMode) {
        onBackgroundClick?.();
      }
      setPanning(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [clampViewport, isBulkPositionEditMode, onBackgroundClick, onRequestCreateAt, panning]);

  useEffect(() => {
    if (!contextMenu) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-map-context-menu="true"]')) {
        return;
      }
      setContextMenu(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContextMenu(null);
      }
    };

    // マップ操作（スクロール/ズーム等）でメニューが残らないように閉じる
    const handleWheel = () => {
      setContextMenu(null);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [contextMenu]);

  const getLocationColor = (location: Location) => {
    if (location.id === selectedLocationId) return '#ff4d4f';
    if (location.id === hoveredLocation) return '#fa8c16';
    const category = location.categoryId ? categoryById.get(location.categoryId) : undefined;
    if (category) return category.color ?? '#ffffff';
    if (location.level === 0) return '#52c41a'; // 工場レベル
    if (location.level === 1) return '#1890ff'; // 棟レベル
    return '#722ed1'; // その他
  };

  const getIconComponent = (iconName?: string | null) => {
    if (!iconName || !(iconName in Icons)) {
      return null;
    }
    const iconMap = Icons as unknown as Record<string, AntIconComponent>;
    const IconComponent = iconMap[iconName];
    if (!IconComponent) {
      return null;
    }
    return IconComponent;
  };

  const getLevelIcon = (location: Location) => {
    const category = location.categoryId ? categoryById.get(location.categoryId) : undefined;
    if (category?.icon) {
      const CategoryIcon = getIconComponent(category.icon);
      if (CategoryIcon) {
        return CategoryIcon;
      }
    }

    // まずカスタムアイコンをチェック
    if (location.icon) {
      const CustomIcon = getIconComponent(location.icon);
      if (CustomIcon) {
        return CustomIcon;
      }
    }

    // カスタムアイコンがない場合はデフォルト
    if (location.mapMode === 'building') return Icons.BuildOutlined as unknown as AntIconComponent;
    if (location.mapMode === 'folder') return Icons.FolderOutlined as unknown as AntIconComponent;
    if (location.level === 0) return HomeOutlined;
    return EnvironmentOutlined;
  };

  const contextMenuItems: MenuProps['items'] = useMemo(() => {
    return [
      {
        key: 'create',
        icon: <PlusOutlined />,
        label: 'ここにエリア追加',
      },
    ];
  }, []);

  return (
    <div>
      {/* 選択中インジケーターのアニメーション */}
      <style>
        {`
          @keyframes selectionPulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>

      {/* ヘッダー統合版の工場レイアウト図 */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <div
          ref={containerRef}
          onPointerDown={(event) => {
            if (event.button !== 0) {
              return;
            }

            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-location-marker="true"]')) {
              return;
            }
            if (target?.closest('[data-map-context-menu="true"]')) {
              return;
            }

            if (!containerRef.current) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            setContextMenu(null);

            const rect = containerRef.current.getBoundingClientRect();
            const localX = event.clientX - rect.left;
            const localY = event.clientY - rect.top;

            let storedX: number | null = null;
            let storedY: number | null = null;
            if (onRequestCreateAt && !isBulkPositionEditMode) {
              const { scale, panX, panY } = viewportRef.current;
              const baseX = (localX - panX) / (scale || 1);
              const baseY = (localY - panY) / (scale || 1);
              const stored = screenToStoredCoordinates(baseX, baseY);
              storedX = stored?.x ?? null;
              storedY = stored?.y ?? null;
            }

            setPanning({
              startClientX: event.clientX,
              startClientY: event.clientY,
              startPanX: viewportRef.current.panX,
              startPanY: viewportRef.current.panY,
              startLocalX: localX,
              startLocalY: localY,
              storedX,
              storedY,
            });
          }}
          onContextMenu={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-map-context-menu="true"]')) {
              return;
            }

            // ブラウザの右クリックメニューは抑止（画像選択等も回避）
            event.preventDefault();

            // 右クリック連打のときに残らないよう、先に閉じる
            setContextMenu(null);

            // いまは「何もないところ」用のメニュー（将来、マーカー用メニューも追加可能）
            if (target?.closest('[data-location-marker="true"]')) {
              return;
            }

            if (!containerRef.current) {
              return;
            }

            if (!onRequestCreateAt) {
              return;
            }

            const rect = containerRef.current.getBoundingClientRect();
            const localX = event.clientX - rect.left;
            const localY = event.clientY - rect.top;

            const { scale, panX, panY } = viewportRef.current;
            const baseX = (localX - panX) / (scale || 1);
            const baseY = (localY - panY) / (scale || 1);
            const stored = screenToStoredCoordinates(baseX, baseY);
            if (!stored) {
              return;
            }

            setContextMenu({
              localX,
              localY,
              storedX: stored.x,
              storedY: stored.y,
            });
          }}
          onDoubleClick={(event) => {
            if (!containerRef.current) {
              return;
            }

            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-location-marker="true"]') || target?.closest('[data-map-context-menu="true"]')) {
              return;
            }

            event.preventDefault();
            const rect = containerRef.current.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const factor = event.shiftKey ? 1 / 1.15 : 1.15;
            zoomAround(viewportRef.current.scale * factor, x, y);
            setContextMenu(null);
          }}
          style={{
            position: 'relative',
            borderRadius: 0,
            overflow: 'hidden',
            boxShadow: 'none',
            width: '100%',
            aspectRatio: '1 / 1',
            cursor: viewport.scale > 1 ? (panning ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {contextMenu && (
            <div
              ref={contextMenuElRef}
              style={{
                position: 'absolute',
                left: (() => {
                  const { width } = getEffectiveDisplaySize();
                  const maxX = Math.max(8, width - 220);
                  return Math.max(8, Math.min(contextMenu.localX + 6, maxX));
                })(),
                top: (() => {
                  const { height } = getEffectiveDisplaySize();
                  const maxY = Math.max(8, height - 140);
                  return Math.max(8, Math.min(contextMenu.localY + 6, maxY));
                })(),
                zIndex: 60,
                minWidth: 200,
                background: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: 8,
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
                padding: 4,
                userSelect: 'auto',
                WebkitUserSelect: 'auto',
                MozUserSelect: 'auto',
                msUserSelect: 'text',
              }}
              data-map-context-menu="true"
            >
              <Menu
                items={contextMenuItems}
                selectable={false}
                onClick={({ key }) => {
                  const current = contextMenuRef.current;
                  if (!current) return;
                  if (key === 'create') {
                    onRequestCreateAt?.(current.storedX, current.storedY);
                    setContextMenu(null);
                  }
                }}
              />
            </div>
          )}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transform: `translate(${viewport.panX}px, ${viewport.panY}px) scale(${viewport.scale})`,
              transformOrigin: '0 0',
            }}
          >
            {resolvedBackgroundImageUrl ? (
              <img
                ref={imageRef}
                src={resolvedBackgroundImageUrl}
                alt="工場レイアウト"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                  background: resolvedBackgroundFill,
                  filter: 'brightness(0.95)'
                } as React.CSSProperties}
                onLoad={handleImageLoad}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: `
                    repeating-linear-gradient(
                      0deg,
                      #e0e0e0 0px,
                      #e0e0e0 1px,
                      transparent 1px,
                      transparent 50px
                    ),
                    repeating-linear-gradient(
                      90deg,
                      #e0e0e0 0px,
                      #e0e0e0 1px,
                      transparent 1px,
                      transparent 50px
                    ),
                    linear-gradient(135deg, rgba(24,144,255,0.08), rgba(24,144,255,0.02))
                  `,
                  position: 'relative'
                }}
              >
                {mapMode !== 'folder' && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    zIndex: 5,
                  }}>
                    <Button
                      size="small"
                      icon={<PictureOutlined />}
                      disabled={!onRequestMapImageSettings}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestMapImageSettings?.();
                      }}
                    >
                      背景画像設定
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* エリアマーカー（配置済み） */}
            {locations
              .filter((location) => hasStoredCoordinates(location))
              .map(location => {
                const basePos = getLocationScreenPosition(location.xCoordinate, location.yCoordinate);

                if (!basePos) return null;

                const tempPos = tempPositions[location.id];
                const pos = tempPos ? tempPos : basePos;

                const isSelected = location.id === selectedLocationId;
                const isHovered = location.id === hoveredLocation;
                const thumbnailUrl = location.thumbnailFileId
                  ? thumbnailUrlByFileId[location.thumbnailFileId]
                  : undefined;

                return (
                  <Tooltip
                    key={location.id}
                    title={
                      <div>
                        <strong>{location.name}</strong>
                        {location.description && (
                          <>
                            <br />
                            {location.description}
                          </>
                        )}
                      </div>
                    }
                    placement="top"
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: pos.x - locationMarkerBoxSize / 2,
                        top: pos.y - locationMarkerBoxSize,
                        width: locationMarkerBoxSize,
                        height: locationMarkerBoxSize,
                        cursor: isBulkPositionEditMode ? 'grab' : 'pointer',
                        zIndex: isSelected || isHovered ? 20 : 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: `scale(${markerCompensationScale})`,
                        transformOrigin: '50% 100%',
                      }}
                      onMouseEnter={() => setHoveredLocation(location.id)}
                      onMouseLeave={() => setHoveredLocation(null)}
                      onClick={(event) => {
                        if (suppressNextMarkerClickRef.current) {
                          suppressNextMarkerClickRef.current = false;
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        onLocationClick?.(location);
                      }}
                      onDoubleClick={(event) => {
                        event.stopPropagation();
                        onLocationDoubleClick?.(location);
                      }}
                      onPointerDown={(event) => {
                        if (!isBulkPositionEditMode) {
                          return;
                        }

                        event.stopPropagation();

                        setDraggingMarker({
                          id: location.id,
                          startClientX: event.clientX,
                          startClientY: event.clientY,
                          startPos: { x: pos.x, y: pos.y },
                          sourceIsUnplaced: false,
                        });
                      }}
                      data-location-marker="true"
                    >
                      {/* ベース（ピン） */}
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 0,
                        }}
                      >
                        <EnvironmentFilled
                          style={{
                            fontSize: locationMarkerFolderIconSize,
                            color: isSelected ? token.colorError : token.colorPrimary,
                            opacity: locationMarkerPinOpacity,
                            filter: isSelected || isHovered
                              ? 'drop-shadow(0 3px 10px rgba(0,0,0,0.38))'
                              : 'drop-shadow(0 3px 8px rgba(0,0,0,0.32))',
                          }}
                        />
                      </div>

                      <div
                        style={{
                          position: 'relative',
                          width: locationMarkerIconBoxSize,
                          height: locationMarkerIconBoxSize,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                          transform: `translateY(-${locationMarkerIconContainerTranslateY}px)`,
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: `calc(50% + ${locationMarkerIconPedestalCenterOffsetY}px)`,
                            width: locationMarkerIconPedestalSize,
                            height: locationMarkerIconPedestalSize,
                            transform: 'translate(-50%, -50%)',
                            borderRadius: '50%',
                            background: '#fff',
                            border: `1px solid ${token.colorBorderSecondary}`,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            pointerEvents: 'none',
                            zIndex: 0,
                          }}
                        />
                        <div
                          style={{
                            position: 'relative',
                            fontSize: locationMarkerIconGlyphSize,
                            color: getLocationColor(location),
                            filter: isSelected || isHovered
                              ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))'
                              : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                            transform: `translateY(${locationMarkerIconYOffset}px)`,
                            zIndex: 1,
                          }}
                        >
                          {React.createElement(getLevelIcon(location))}
                        </div>
                        {thumbnailUrl && (
                          <img
                            src={thumbnailUrl}
                            alt={location.name}
                            draggable={false}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid white',
                              boxShadow: isSelected || isHovered
                                ? '0 3px 6px rgba(0,0,0,0.28)'
                                : '0 2px 4px rgba(0,0,0,0.2)',
                              zIndex: 2,
                            }}
                            onError={(event) => {
                              (event.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                      </div>

                      {/* オブジェクト数バッジ（設備 + 部品・消耗品） */}
                      {typeof location.objectCount === 'number' && location.objectCount > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: locationMarkerBadgeOffsetY,
                            right: locationMarkerBadgeOffsetX,
                            background: '#1677ff',
                            color: 'white',
                            borderRadius: '50%',
                            width: locationMarkerBadgeSize,
                            height: locationMarkerBadgeSize,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: locationMarkerBadgeFontSize,
                            fontWeight: 'bold',
                            border: `${locationMarkerBadgeBorderWidth}px solid white`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            zIndex: 3,
                          }}
                        >
                          {location.objectCount > 99 ? '99+' : location.objectCount}
                        </div>
                      )}

                      {/* エリア名ラベル（常時表示） */}
                      <div
                        style={{
                          position: 'absolute',
                          top: locationPinLabelTop,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          fontSize: locationMarkerLabelFontSize,
                          fontWeight: isSelected ? 'bold' : 'normal',
                          textShadow: '0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.6)',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {location.name}
                      </div>

                      {/* 選択中インジケーター（角カーソル） */}
                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: locationMarkerSelectionIndicatorSize.top,
                            left: locationMarkerSelectionIndicatorSize.left,
                            width: locationMarkerSelectionIndicatorSize.width,
                            height: locationMarkerSelectionIndicatorSize.height,
                            pointerEvents: 'none',
                            animation: 'selectionPulse 2s ease-in-out infinite',
                            filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))',
                            zIndex: 3,
                          }}
                        >
                          {/* 左上 */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 17,
                            height: 17,
                            borderTop: '3px solid #fff',
                            borderLeft: '3px solid #fff',
                            borderRadius: '4px 0 0 0',
                          }} />
                          {/* 右上 */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 17,
                            height: 17,
                            borderTop: '3px solid #fff',
                            borderRight: '3px solid #fff',
                            borderRadius: '0 4px 0 0',
                          }} />
                          {/* 左下 */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: 17,
                            height: 17,
                            borderBottom: '3px solid #fff',
                            borderLeft: '3px solid #fff',
                            borderRadius: '0 0 0 4px',
                          }} />
                          {/* ‰E‰º */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 17,
                            height: 17,
                            borderBottom: '3px solid #fff',
                            borderRight: '3px solid #fff',
                            borderRadius: '0 0 4px 0',
                          }} />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
          </div>

          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 35,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              background: 'rgba(255,255,255,0.9)',
              padding: '6px 8px',
              borderRadius: 10,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            }}
          >
            <Button
              size="small"
              icon={<MinusOutlined />}
              onClick={() => {
                const { width, height } = getEffectiveDisplaySize();
                zoomAround(viewportRef.current.scale / 1.15, width / 2, height / 2);
              }}
              disabled={viewport.scale <= 1}
            />
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                const { width, height } = getEffectiveDisplaySize();
                zoomAround(viewportRef.current.scale * 1.15, width / 2, height / 2);
              }}
            />
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => setViewport({ scale: 1, panX: 0, panY: 0 })}
              disabled={viewport.scale === 1 && viewport.panX === 0 && viewport.panY === 0}
            />
          </div>

          {/* ヘッダータイトル */}
          {showTitle && (
            <div
              style={{
                position: 'absolute',
                top: '30px',
                left: '40px',
                color: 'white',
                zIndex: 10
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontSize: '36px',
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                エリア管理
              </h1>
            </div>
          )}

          {/* エリアマーカー（未配置: 画面下部に固定表示） */}
          {unplacedGridItems.map((item, index) => {
            const pos = getUnplacedScreenPosition(index);
            if (!pos) return null;

            const isSelected = Boolean(item.selected);
            const isHovered = item.id === hoveredLocation;

            const IconComponent = item.icon
              ? (Icons[item.icon as keyof typeof Icons] as React.ComponentType | null)
              : (Icons.AppstoreOutlined as React.ComponentType);

            const badgeText = item.badgeCount > 99 ? '99+' : String(item.badgeCount);

            return (
              <Tooltip
                key={item.id}
                title={item.tooltip ?? (
                  <div>
                    <strong>{item.label}</strong>
                    <br />
                    件数: {item.badgeCount}
                  </div>
                )}
                placement="top"
              >
                <div
                  style={{
                    position: 'absolute',
                    left: pos.x - locationMarkerBoxSize / 2,
                    top: pos.y - locationMarkerBoxSize,
                    width: locationMarkerBoxSize,
                    height: locationMarkerBoxSize,
                    cursor: 'pointer',
                    zIndex: isSelected || isHovered ? 20 : 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={() => setHoveredLocation(item.id)}
                  onMouseLeave={() => setHoveredLocation(null)}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    item.onClick?.();
                  }}
                  data-location-marker="true"
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    <div
                      style={{
                        width: locationMarkerUnplacedTileSize,
                        height: locationMarkerUnplacedTileSize,
                        borderRadius: locationMarkerUnplacedTileBorderRadius,
                        border: `1px solid ${isSelected ? token.colorPrimary : token.colorBorder}`,
                        background: isSelected ? token.colorPrimaryBg : token.colorBgContainer,
                        boxShadow: isSelected || isHovered
                          ? '0 3px 8px rgba(0,0,0,0.25)'
                          : '0 2px 6px rgba(0,0,0,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      width: locationMarkerIconBoxSize,
                      height: locationMarkerIconBoxSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <div
                      style={{
                        fontSize: locationMarkerIconGlyphSize,
                        color: item.color || '#1677ff',
                        filter: isSelected || isHovered
                          ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))'
                          : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        transform: `translateY(${locationMarkerIconYOffset}px)`,
                      }}
                    >
                      {React.createElement(IconComponent)}
                    </div>
                  </div>

                  {item.badgeCount > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: locationMarkerBadgeOffsetY,
                        right: locationMarkerBadgeOffsetX,
                        background: '#1677ff',
                        color: 'white',
                        borderRadius: '50%',
                        width: locationMarkerBadgeSize,
                        height: locationMarkerBadgeSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: locationMarkerBadgeFontSize,
                        fontWeight: 'bold',
                        border: `${locationMarkerBadgeBorderWidth}px solid white`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        zIndex: 3,
                      }}
                    >
                      {badgeText}
                    </div>
                  )}

                  <div
                    style={{
                      position: 'absolute',
                      top: locationPinLabelTop,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      fontSize: locationMarkerLabelFontSize,
                      fontWeight: isSelected ? 'bold' : 'normal',
                      textShadow: '0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.6)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {item.label}
                  </div>

                  {isSelected && (
                    <div
                      style={{
                        position: 'absolute',
                        top: locationMarkerSelectionIndicatorSize.top,
                        left: locationMarkerSelectionIndicatorSize.left,
                        width: locationMarkerSelectionIndicatorSize.width,
                        height: locationMarkerSelectionIndicatorSize.height,
                        pointerEvents: 'none',
                        animation: 'selectionPulse 2s ease-in-out infinite',
                        filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))',
                        zIndex: 3,
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 17,
                        height: 17,
                        borderTop: '3px solid #fff',
                        borderLeft: '3px solid #fff',
                        borderRadius: '4px 0 0 0',
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 17,
                        height: 17,
                        borderTop: '3px solid #fff',
                        borderRight: '3px solid #fff',
                        borderRadius: '0 4px 0 0',
                      }} />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: 17,
                        height: 17,
                        borderBottom: '3px solid #fff',
                        borderLeft: '3px solid #fff',
                        borderRadius: '0 0 0 4px',
                      }} />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 17,
                        height: 17,
                        borderBottom: '3px solid #fff',
                        borderRight: '3px solid #fff',
                        borderRadius: '0 0 4px 0',
                      }} />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}

          {unplacedLocations.map(location => {
            const unplacedIndex = unplacedIndexById.get(location.id);
            const basePos = typeof unplacedIndex === 'number'
              ? getUnplacedScreenPosition(unplacedIndex + unplacedGridOffset)
              : null;

            if (!basePos) return null;

            const tempPos = tempPositions[location.id];
            const pos = tempPos ? tempPos : basePos;

            const isSelected = location.id === selectedLocationId;
            const isHovered = location.id === hoveredLocation;

            return (
              <Tooltip
                key={location.id}
                title={
                  <div>
                    <strong>{location.name}</strong>
                    <>
                      <br />
                      未配置（座標未設定）
                    </>
                    {location.description && (
                      <>
                        <br />
                        {location.description}
                      </>
                    )}
                  </div>
                }
                placement="top"
              >
                <div
                  style={{
                    position: 'absolute',
                    left: pos.x - locationMarkerBoxSize / 2,
                    top: pos.y - locationMarkerBoxSize,
                    width: locationMarkerBoxSize,
                    height: locationMarkerBoxSize,
                    cursor: isBulkPositionEditMode ? 'grab' : 'pointer',
                    zIndex: isSelected || isHovered ? 20 : 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={() => setHoveredLocation(location.id)}
                  onMouseLeave={() => setHoveredLocation(null)}
                  onClick={(event) => {
                    if (suppressNextMarkerClickRef.current) {
                      suppressNextMarkerClickRef.current = false;
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                    onLocationClick?.(location);
                  }}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    onLocationDoubleClick?.(location);
                  }}
                  onPointerDown={(event) => {
                    if (!isBulkPositionEditMode) {
                      return;
                    }

                    event.stopPropagation();

                    setDraggingMarker({
                      id: location.id,
                      startClientX: event.clientX,
                      startClientY: event.clientY,
                      startPos: { x: pos.x, y: pos.y },
                      sourceIsUnplaced: true,
                    });
                  }}
                  data-location-marker="true"
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    <EnvironmentFilled
                      style={{
                        fontSize: locationMarkerFolderIconSize,
                        color: isSelected ? token.colorError : token.colorPrimary,
                        opacity: locationMarkerPinOpacity,
                        filter: isSelected || isHovered
                          ? 'drop-shadow(0 3px 10px rgba(0,0,0,0.38))'
                          : 'drop-shadow(0 3px 8px rgba(0,0,0,0.32))',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      width: locationMarkerIconBoxSize,
                      height: locationMarkerIconBoxSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      transform: `translateY(-${locationMarkerIconContainerTranslateY}px)`,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: `calc(50% + ${locationMarkerIconPedestalCenterOffsetY}px)`,
                        width: locationMarkerIconPedestalSize,
                        height: locationMarkerIconPedestalSize,
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        background: '#fff',
                        border: `1px solid ${token.colorBorderSecondary}`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />
                    <div
                      style={{
                        position: 'relative',
                        fontSize: locationMarkerIconGlyphSize,
                        color: getLocationColor(location),
                        filter: isSelected || isHovered
                          ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))'
                          : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        transform: `translateY(${locationMarkerIconYOffset}px)`,
                        zIndex: 1,
                      }}
                    >
                      {React.createElement(getLevelIcon(location))}
                    </div>
                  </div>

                  {/* エリア名ラベル（常時表示） */}
                  <div
                    style={{
                      position: 'absolute',
                      top: locationPinLabelTop,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      fontSize: locationMarkerLabelFontSize,
                      fontWeight: isSelected ? 'bold' : 'normal',
                      textShadow: '0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.6)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {location.name}
                  </div>

                  {/* 選択中インジケーター（角カーソル） */}
                  {isSelected && (
                    <div
                      style={{
                        position: 'absolute',
                        top: locationMarkerSelectionIndicatorSize.top,
                        left: locationMarkerSelectionIndicatorSize.left,
                        width: locationMarkerSelectionIndicatorSize.width,
                        height: locationMarkerSelectionIndicatorSize.height,
                        pointerEvents: 'none',
                        animation: 'selectionPulse 2s ease-in-out infinite',
                        filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))',
                        zIndex: 3,
                      }}
                    >
                      {/* 左上 */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 17,
                        height: 17,
                        borderTop: '3px solid #fff',
                        borderLeft: '3px solid #fff',
                        borderRadius: '4px 0 0 0',
                      }} />
                      {/* 右上 */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 17,
                        height: 17,
                        borderTop: '3px solid #fff',
                        borderRight: '3px solid #fff',
                        borderRadius: '0 4px 0 0',
                      }} />
                      {/* 左下 */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: 17,
                        height: 17,
                        borderBottom: '3px solid #fff',
                        borderLeft: '3px solid #fff',
                        borderRadius: '0 0 0 4px',
                      }} />
                      {/* 右下 */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 17,
                        height: 17,
                        borderBottom: '3px solid #fff',
                        borderRight: '3px solid #fff',
                        borderRadius: '0 0 4px 0',
                      }} />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}

          {bottomLeftOverlay ? (
            <div
              style={{
                position: 'absolute',
                left: 12,
                bottom: 12,
                zIndex: 45,
                pointerEvents: 'auto',
                userSelect: 'auto',
                WebkitUserSelect: 'auto',
                MozUserSelect: 'auto',
                msUserSelect: 'text',
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.preventDefault()}
            >
              {bottomLeftOverlay}
            </div>
          ) : null}

          {bottomRightOverlay ? (
            <div
              style={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                zIndex: 45,
                pointerEvents: 'auto',
                userSelect: 'auto',
                WebkitUserSelect: 'auto',
                MozUserSelect: 'auto',
                msUserSelect: 'text',
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.preventDefault()}
            >
              {bottomRightOverlay}
            </div>
          ) : null}
        </div>
      </div>

    </div>
  );
}
